import { useState, useRef } from 'react'
import cx from 'classnames'
import Styles from './EmptyPromptBar.module.scss'
import { PromptInput } from 'components/PromptInput/PromptInput'
import { EditorView } from 'prosemirror-view'
import { NodeSelection } from 'prosemirror-state'
import { markdownSerializer } from 'common/Prose/components/TextInlineFormatBar/components/FormatBars/markdownSerializer'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { injectContent } from 'common/Prose/utils/injectContent'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { useCreateAISuggestionNode } from 'hooks/queries/layers/aiSuggestionNodes/useCreateAISuggestionNode'
import { useFocusWithin, useInteractOutside } from '@react-aria/interactions'
import { BrainstormSchema } from 'components/Wizards/schemas/Brainstorm'
import { Field as BaseField } from 'components/Wizards/Field/Field'
import { StringInput } from 'components/Wizards/Wizard/Form/StringInput'
import { Formik } from 'formik'
import { Form } from 'components/Wizards/Form/Form'
import { noop } from 'lodash'
import { IdeasCount } from 'components/BlazeCommandLine/components/NumberOfIdeasSelect'
import { reportError } from 'utils/error'

interface Props {
  articleId: string
  layerId: string
  view: EditorView
}

interface Values {
  content_type?: string
  topic?: string
}

export const EmptyPromptBar = (props: Props) => {
  const [containerFocused, setContainerFocused] = useState(true)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const handlePromptFocused = (isFocused: boolean) => {
    if (isFocused) setContainerFocused(isFocused)
  }

  const handleClickOutside = () => {
    setContainerFocused(false)
  }

  useInteractOutside({ ref: wrapperRef, onInteractOutside: handleClickOutside })

  return (
    <div className={cx(Styles.ideasWrapper, { [Styles.focused]: containerFocused })} ref={wrapperRef}>
      {!containerFocused && (
        <PromptInput
          className={Styles.promptInput}
          prompt=""
          onChange={noop}
          onSubmit={noop}
          onFocusChange={handlePromptFocused}
          placeholder="Give me ideas about... "
          largeInput
          withStartIcon
        />
      )}
      {containerFocused && <EmptyPromptBarForm {...props} />}
    </div>
  )
}

export const EmptyPromptBarForm = ({ view, articleId, layerId }: Props) => {
  const generateMutation = useCreateAISuggestionNode({ articleId, layerId })

  const schema = BrainstormSchema
  const contentTypeField = Object.entries(schema.fields).find(([name]) => name === 'content_type')
  const topicField = Object.entries(schema.fields).find(([name]) => name === 'topic')

  const [hasFocusWithin, setHasFocusWithin] = useState(false)
  const { focusWithinProps } = useFocusWithin({
    onFocusWithinChange: (isFocusWithin) => setHasFocusWithin(isFocusWithin),
  })

  const handleGenerate = ({ content_type, topic }: Values) => {
    const selection = new NodeSelection(view.state.doc.resolve(0))
    const newDoc = injectContent(view.state, selection, '<<AI_CONTENT>>')
    const documentAsMarkdown = markdownSerializer.serialize(newDoc)

    generateMutation.mutate(
      {
        selection,
        documentAsMarkdown,
        prompt: {
          generation_type: GenerationType.Brainstorm,
          ideas_count: IdeasCount.S,
          content_type,
          topic,
        },
      },
      {
        onError: (error) => {
          reportError(error)
          showToastMessage('An error occurred when generating your content', 'error')
        },
      },
    )
  }

  return (
    <Formik initialValues={{ topic: '' }} onSubmit={(values) => handleGenerate(values)}>
      {({ values }) => (
        <div {...focusWithinProps} tabIndex={-1}>
          <Form.Fields className={Styles.fields}>
            {topicField && (
              <div className={Styles.fieldContainer}>
                <BaseField label="Give me ideas about..." isRequired>
                  <StringInput
                    key={`focus-${hasFocusWithin}`}
                    name={topicField[0]}
                    schema={topicField[1].meta({
                      generate: false,
                      snippets: false,
                      typeahead: false,
                      rows: hasFocusWithin ? 4 : 1,
                    })}
                    autoFocus={hasFocusWithin}
                  />
                </BaseField>
              </div>
            )}

            {hasFocusWithin && (
              <>
                {contentTypeField && (
                  <div className={Styles.pillsInputContainer}>
                    <BaseField label={contentTypeField[1].describe().meta.label} isRequired>
                      <StringInput
                        name={contentTypeField[0]}
                        schema={contentTypeField[1].meta({ pillsCarousel: true })}
                      />
                    </BaseField>
                  </div>
                )}
                <div className={Styles.buttonsContainer}>
                  <Form.PrimaryActions>
                    <Form.SubmitButton isDisabled={!(values as Values).topic || !(values as Values).content_type}>
                      Generate Ideas
                    </Form.SubmitButton>
                  </Form.PrimaryActions>
                </div>
              </>
            )}
          </Form.Fields>
        </div>
      )}
    </Formik>
  )
}
